import { MathUtils, PerspectiveCamera, Vector3 } from 'three';

export interface CameraDrive {
  /** Hare world position (interpolated). */
  target: Vector3;
  forward: Vector3;
  right: Vector3;
  speed: number;
  steer: number;
  /** Height above the ground under the pearl. */
  airHeight: number;
  /** Ground height ~40 m ahead (for anticipating crests/valleys). */
  groundAhead: number;
  /** Ground height under the camera's chase position (keeps it out of hillsides). */
  groundBehind: number;
  mode: 'menu' | 'play' | 'crash';
  time: number;
  /** Unscaled frame time (s). Shake runs on real time so slow motion never stretches it into a sway. */
  realDt?: number;
}

const _v = new Vector3();
const _menuOff = new Vector3();
const _chaseOff = new Vector3();
const _off = new Vector3();
const _lookOff = new Vector3();

/**
 * Chase camera that springs its *offset* from the pearl (not its world position), so it stays
 * locked on at 90 m/s yet still eases into turns, lifts over jumps, widens its lens with speed,
 * banks, and shakes on impacts (trauma model). Also frames the idle menu and crash shots.
 */
export class ChaseCamera {
  readonly camera: PerspectiveCamera;
  private offset = new Vector3();
  private offsetVel = new Vector3();
  private lookOffset = new Vector3();
  private lookVel = new Vector3();
  private smoothY = 0;
  private smoothYVel = 0;
  private fov = 52;
  private roll = 0;
  private trauma = 0;
  private shakeClock = 0;
  private blend = 0;
  private initialised = false;
  private readonly crashAnchor = new Vector3();
  private readonly crashPos = new Vector3();

  constructor(aspect: number) {
    this.camera = new PerspectiveCamera(52, aspect, 0.15, 3200);
  }

  /** Brief lens punch (wind gates, boosts). */
  kick(amount: number): void {
    this.fovKick = Math.min(1.5, this.fovKick + amount);
  }
  private fovKick = 0;

  shake(amount: number): void {
    this.trauma = Math.min(1, this.trauma + amount);
  }

  snap(): void {
    this.initialised = false;
  }

  startCrash(at: Vector3): void {
    this.crashAnchor.copy(at);
    this.crashPos.copy(this.camera.position);
  }

  update(d: CameraDrive, dt: number): void {
    const speedN = MathUtils.clamp((d.speed - 12) / 70, 0, 1);
    const playTarget = d.mode === 'menu' ? 0 : 1;
    this.blend += (playTarget - this.blend) * Math.min(1, dt * (playTarget > this.blend ? 2.4 : 1.5));
    const b = smootherstep(this.blend);

    if (!this.initialised) {
      this.smoothY = d.target.y;
      this.smoothYVel = 0;
    }
    // Vertical follows with a little give so landings and bumps don't jolt the frame.
    const yres = springScalar(this.smoothY, this.smoothYVel, d.target.y, d.mode === 'menu' ? 4 : 7, dt);
    this.smoothY = MathUtils.clamp(yres[0], d.target.y - 2.5, d.target.y + 2.5);
    this.smoothYVel = yres[1];

    // --- Menu offset: just behind the pearl and a little to the side, looking north the way it
    // will roll — the doorway on the horizon in view, and almost no swing when the run starts.
    _menuOff
      .set(0, 0, 0)
      .addScaledVector(d.forward, -4.4 + Math.sin(d.time * 0.21) * 0.2)
      .addScaledVector(d.right, 1.25 + Math.sin(d.time * 0.17) * 0.25);
    _menuOff.y = 1.55 + Math.sin(d.time * 0.29) * 0.1;

    // --- Chase offset.
    const air = Math.min(d.airHeight, 18);
    const dist = 6.0 + speedN * 2.4;
    const height = 2.6 + speedN * 1.0 + air * 0.12;
    _chaseOff.set(0, 0, 0).addScaledVector(d.forward, -dist).addScaledVector(d.right, d.steer * 0.6);
    _chaseOff.y = Math.max(height - air * 0.22, d.groundBehind + 2.4 - this.smoothY, 1.2);

    // Blend by orbiting around the pearl (cylindrical lerp), never straight through it.
    if (b <= 0.001) _off.copy(_menuOff);
    else if (b >= 0.999) _off.copy(_chaseOff);
    else {
      const a0 = Math.atan2(_menuOff.z, _menuOff.x);
      let da = Math.atan2(_chaseOff.z, _chaseOff.x) - a0;
      while (da > Math.PI) da -= Math.PI * 2;
      while (da < -Math.PI) da += Math.PI * 2;
      const ang = a0 + da * b;
      const rad = MathUtils.lerp(Math.hypot(_menuOff.x, _menuOff.z), Math.hypot(_chaseOff.x, _chaseOff.z), b) + Math.sin(b * Math.PI) * 1.4;
      _off.set(Math.cos(ang) * rad, MathUtils.lerp(_menuOff.y, _chaseOff.y, b) + Math.sin(b * Math.PI) * 1.5, Math.sin(ang) * rad);
    }

    // --- Look offset.
    // Narrow (portrait) screens pull the pearl toward the centre; wide screens give the world more room.
    const wide = MathUtils.clamp((this.camera.aspect - 0.6) / 1.2, 0, 1);
    const menuLook = _v.copy(d.right).multiplyScalar(MathUtils.lerp(-0.1, -0.9, wide)).addScaledVector(d.forward, 9);
    // Portrait: aim a little low so the pearl sits above the menu dock.
    menuLook.y = MathUtils.lerp(-0.3, 0.85, wide);
    const lookAhead = 6 + d.speed * 0.1;
    const chaseLookY = MathUtils.lerp(0.7, d.groundAhead + 0.7 - this.smoothY, 0.25 + Math.min(air / 18, 1) * 0.25);
    _lookOff.copy(d.forward).multiplyScalar(lookAhead).lerp(menuLook, 1 - b);
    _lookOff.y = MathUtils.lerp(menuLook.y, chaseLookY, b);

    if (!this.initialised) {
      this.offset.copy(_off);
      this.lookOffset.copy(_lookOff);
      this.offsetVel.set(0, 0, 0);
      this.lookVel.set(0, 0, 0);
      this.initialised = true;
    }
    spring(this.offset, this.offsetVel, _off, d.mode === 'menu' ? 5 : 8, dt);
    spring(this.lookOffset, this.lookVel, _lookOff, 10, dt);

    const cam = this.camera;
    if (d.mode === 'crash') {
      // Ease out to a wide shot of the tumble.
      _v.copy(this.crashAnchor).addScaledVector(d.forward, -8);
      _v.y += 3.4;
      this.crashPos.lerp(_v, Math.min(1, dt * 1.6));
      cam.position.copy(this.crashPos);
      _v.copy(d.target);
      _v.y += 0.5;
    } else {
      cam.position.set(d.target.x, this.smoothY, d.target.z).add(this.offset);
      _v.set(d.target.x, this.smoothY, d.target.z).add(this.lookOffset);
    }

    this.fovKick = Math.max(0, this.fovKick - dt * 2.2);
    const targetFov = MathUtils.lerp(50, 56 + speedN * 26, b) + this.fovKick * 9;
    this.fov += (targetFov - this.fov) * Math.min(1, dt * 3);
    this.roll += (-d.steer * 0.07 * b * (0.4 + speedN) - this.roll) * Math.min(1, dt * 5);

    // Impact shake: a short, sharp, high-frequency kick on *real* time — during the crash
    // slow-motion a game-time shake would stretch into a slow back-and-forth sway.
    const rdt = d.realDt ?? dt;
    this.shakeClock += rdt;
    this.trauma = Math.max(0, this.trauma - rdt * 2.6);
    const s = this.trauma * this.trauma;
    const t = this.shakeClock * 40;
    const shakeX = (Math.sin(t * 1.3) + Math.sin(t * 2.7 + 1)) * 0.5 * s * 0.3;
    const shakeY = (Math.sin(t * 1.7 + 2) + Math.sin(t * 3.1)) * 0.5 * s * 0.3;
    // Speed buzz only while actually running.
    const buzz = d.mode === 'play' ? speedN * speedN * 0.02 * b : 0;
    cam.position.addScaledVector(d.right, shakeX + Math.sin(d.time * 47) * buzz);
    cam.position.y += shakeY + Math.sin(d.time * 53 + 1) * buzz;
    cam.up.set(0, 1, 0);
    cam.lookAt(_v);
    cam.rotateZ(this.roll + s * Math.sin(t * 2.3) * 0.03);
    if (Math.abs(cam.fov - this.fov) > 0.01) {
      cam.fov = this.fov;
      cam.updateProjectionMatrix();
    }
  }

  setAspect(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }
}

/** Implicit (unconditionally stable) critically-damped spring step on a vector. */
const AXES = ['x', 'y', 'z'] as const;
function spring(x: Vector3, v: Vector3, target: Vector3, omega: number, dt: number): void {
  for (const k of AXES) {
    const r = springScalar(x[k], v[k], target[k], omega, dt);
    x[k] = r[0];
    v[k] = r[1];
  }
}

const _sr: [number, number] = [0, 0];
function springScalar(x: number, v: number, target: number, omega: number, dt: number): [number, number] {
  const f = 1 + 2 * dt * omega;
  const hoo = dt * omega * omega;
  const hhoo = dt * hoo;
  const detInv = 1 / (f + hhoo);
  _sr[0] = (f * x + dt * v + hhoo * target) * detInv;
  _sr[1] = (v + hoo * (target - x)) * detInv;
  return _sr;
}

function smootherstep(t: number): number {
  t = Math.min(1, Math.max(0, t));
  return t * t * t * (t * (t * 6 - 15) + 10);
}
